import React, { useState } from 'react';
import { getUserId } from '../config/constants';
import { carService } from '../services/carService';
import { clientService } from '../services/clientService';
import { orderService } from '../services/orderService';
import { supplierService } from '../services/supplierService';
import { FaEdit } from 'react-icons/fa';
import { GrSync } from "react-icons/gr";
import { formatTimestamp } from '../utils/formatDate';
import { notifyError, notifyInfo, notifySuccess, notifyWarning } from '../utils/notifications';
import { getItemTypeText } from '../utils/UIItemsTypes';
import { getProgressStateText } from '../utils/UITextStates';
import CarInfo from './CarInfo';
import ClientInfo from './ClientInfo';
import GenericTable from './GenericTable';

export default function OrderDetails({ isOpen, onClose, orderData, updateOrderData, submitHandler }) {
    const userID = getUserId();

    const [carId, setCarId] = useState('');
    const [clientId, setClientId] = useState('');
    const [repairs, setRepairs] = useState([]);
    const [items, setItems] = useState([]);
    const [clientData, setClientData] = useState({});
    const [carData, setCarData] = useState({});
    const [clientModal, isOpenClientModal] = useState(false);
    const [carModal, isOpenCarModal] = useState(false);
    const [totalItems, setTotalItems] = useState(0);  // TODO: Generar una tabla snapshot en el backend para modificar la consulta a la DB y guardar valores completos.
    const [totalRepairs, setTotalRepairs] = useState(0);
    const [totalPrice, setTotalPrice] = useState(orderData.total);

    const [allClients, setAllClients] = useState([]);
    const [originalClientId, setOriginalClientId] = useState('');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [hasClientChanged, setHasClientChanged] = useState(false);
    const [allSuppliers, setAllSuppliers] = useState([]);
    const [supplierErrorMessage, setSupplierErrorMessage] = useState('');

    // Estados para modo edición
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [userRole, setUserRole] = useState('');

    // Estados para CRU de Repairs y Items
    const [showRepairForm, setShowRepairForm] = useState(false);
    const [showItemForm, setShowItemForm] = useState(false);
    const [editingRepair, setEditingRepair] = useState(null);
    const [editingItem, setEditingItem] = useState(null);

    // Estado para confirmaciones de acciones sensibles
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        message: '',
        onConfirm: null,
    });

    // Obtener rol del usuario al cargar
    React.useEffect(() => {
        const role = localStorage.getItem('rol');
        setUserRole(role);
    }, []);

    // Helper para verificar si el usuario es admin o owner
    const isAdminOrOwner = () => {
        return userRole === 'Administrador' || userRole === 'owner';
    };

    // Verificar si la orden está completa (pagada o no pagada)
    const isOrderComplete = orderData.processState === 'COMPLETE_NOT_PAY' || orderData.processState === 'COMPLETE_AND_PAY';

    React.useEffect(() => {
        if (isOpen && orderData) {
            fetchAllOrderDetails(orderData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orderData]);


    const fetchAllOrderDetails = async (order) => {
        if (order) {
            try {

                const orderId = order.noOrden !== undefined ? order.noOrden : '';
                const workshopId = order.workshopID !== undefined ? order.workshopID : '';

                setTotalPrice(order.total);

                // Obtener detalles de la orden
                const data = await orderService.getOrderDetails(orderId, workshopId);

                if (data) {
                    const repairs = [];
                    const items = [];

                    setCarId(data.car_id);
                    setClientId(data.client_id);
                    setSelectedClientId(data.client_id);
                    setOriginalClientId(data.client_id);

                    // Paralelizar todas las llamadas que no dependen de data.client_id
                    const [clientData, allClients, suppliersCache] = await Promise.all([
                        // Obtener datos del cliente específico
                        data.client_id ? getDataClient(order, data.client_id) : Promise.resolve(null),
                        // Obtener todos los clientes para reasignación
                        fetchClients(workshopId),
                        // Obtener todos los proveedores
                        fetchSuppliers(workshopId)
                    ]);

                    // Resto del código sigue igual...


                    data.repairs.forEach((repair) => {
                        const cleanedRepair = {
                            repair_id: repair.id, // Guardar ID para edición/eliminación
                            titulo: repair.titulo || '',
                            description: repair.description || '',
                            progress_state: getProgressStateText(repair.progress_state) || '',
                            progress_state_code: repair.progress_state || '', // Guardar código original
                            init_date: formatTimestamp(new Date(repair.init_date)) || '',
                            finish_date: repair.finish_date ? formatTimestamp(new Date(repair.finish_date)) : '',
                            labor_hand: repair.labor_hand || 0,
                            total: repair.total || 0
                        };
                        repairs.push(cleanedRepair);
                    });

                    data.items.forEach((item) => {
                        const normalizedSupplierId =
                            item.supplier_id ||
                            item.supplierId ||
                            null;

                        const matchedSupplier = (suppliersCache || []).find(
                            (supplier) => String(supplier.id) === String(normalizedSupplierId),
                        );

                        const supplierName = matchedSupplier?.name || item?.name || 'Desconocido';

                        const cleanedItem = {
                            item_id: item.id || item.item_id,
                            repair_id: item.repair_id,
                            supplier_id: normalizedSupplierId,
                            supplier_name: supplierName,
                            folio: item.folio || '',
                            description: item.description || '',
                            price: item.price || 0,
                            type: getItemTypeText(item.type) || '',
                            type_code: item.type || '',
                            created_at: item.created_at ? formatTimestamp(new Date(item.created_at)) : '',
                            limit_date_to_pay: item.limit_date_to_pay ? formatTimestamp(new Date(item.limit_date_to_pay)) : ''
                        };
                        items.push(cleanedItem);
                    });

                    setRepairs(repairs);
                    setItems(items);

                }
            } catch (error) {
                console.error('Ocurrio un error al conseguir los de talles de la orden', error);
            }
        }
    };

    const fetchClients = async (workshopId) => {
        try {
            const customers = await clientService.getClients(workshopId);
            setAllClients(customers || []);
        } catch (error) {
            console.error('Error cargando clientes:', error);
        }
    };

    const fetchSuppliers = async (workshopId) => {
        try {
            const suppliers = await orderService.getSuppliers(workshopId);
            const normalizedSuppliers = suppliers || [];
            setAllSuppliers(normalizedSuppliers);

            if (!normalizedSuppliers.length) {
                setSupplierErrorMessage('No hay proveedores registrados en este taller. Por favor, registra uno primero.');
            } else {
                setSupplierErrorMessage('');
            }

            return normalizedSuppliers;
        } catch (error) {
            console.error('Error cargando proveedores:', error);
            setAllSuppliers([]);
            setSupplierErrorMessage('Error al cargar proveedores del taller.');
            return [];
        }
    };

    const openConfirm = (message, onConfirm) => {
        setConfirmConfig({
            open: true,
            message,
            onConfirm,
        });
    };

    const handleCancelConfirm = () => {
        setConfirmConfig({ open: false, message: '', onConfirm: null });
    };

    const handleConfirmAction = async () => {
        if (confirmConfig.onConfirm) {
            try {
                await confirmConfig.onConfirm();
            } finally {
                setConfirmConfig({ open: false, message: '', onConfirm: null });
            }
        } else {
            setConfirmConfig({ open: false, message: '', onConfirm: null });
        }
    };

    const getDataClient = async (order, customClientId = null) => {

        if (order) {
            try {
                const idToUse = customClientId || clientId;

                const responseData = await clientService.getClientById(idToUse);
                const clientInfo = Array.isArray(responseData) ? responseData[0] : responseData;

                if (clientInfo) {
                    setClientData(clientInfo);
                    setCarData(clientInfo);
                    return clientInfo;
                }

                return null;


            } catch (error) {
                console.error('Ocurrio un error al conseguir los de talles de la orden', error);
            }
        }
    };

    const getDataCar = async (order) => {
        if (order) {
            try {
                const workshopId = order.workshopID !== undefined ? order.workshopID : '';
                const responseData = await carService.getCarById(carId, workshopId);
                const carInfo = Array.isArray(responseData) ? responseData[0] : responseData;

                if (carInfo) {
                    setCarData(carInfo);
                    return carInfo;
                }

                return null;

            } catch (error) {
                console.error('Ocurrio un error al conseguir los de talles de la orden', error);
            }
        }
    };

    const savePrices = async () => {
        try {
            let total = 0;
            let sumA = 0, sumB = 0;

            for (const key in repairs) {
                const element = repairs[key];
                if (element.labor_hand) {
                    total += parseFloat(element.labor_hand);
                    sumA += parseFloat(element.labor_hand);
                }
            }

            setTotalRepairs(sumA);

            for (const key in items) {
                const element = items[key];
                if (element.price > 0) {
                    total += parseFloat(element.price);
                    sumB += parseFloat(element.price);
                }
            }

            setTotalItems(sumB);

            const orderId = orderData.noOrden !== undefined ? orderData.noOrden : '';
            const workshopId = orderData.workshopID !== undefined ? orderData.workshopID : '';

            const payload = {
                data: {
                    total: total,
                    process_state: 'REVIEW',
                }
            };

            await orderService.updateOrder(orderId, payload, workshopId);
            setTotalPrice(`$${total} MXN`);
            updateOrderData('total', `$${total} MXN`);
            updateOrderData('estadoProceso', 'En revisión');
            updateOrderData('processState', 'REVIEW');
            notifyInfo(`Mano de obra calculada: $${total} MXN`);
        } catch (error) {
            console.error('Error al calcular:', error);
            notifyError('No se pudo calcular el precio');
        }
    }
    // Funciones de edición
    const handleEdit = () => {
        // Verificar si la orden está pagada y el usuario no es admin ni owner
        if (orderData.processState === 'COMPLETE_AND_PAY' && !isAdminOrOwner()) {
            notifyWarning('Solo los administradores y dueños pueden editar órdenes pagadas');
            return;
        }

        setIsEditMode(true);
        const currentData = {
            serviciosSolicitados: orderData.serviciosSolicitados || '',
            requiereFactura: orderData.requiereFactura || 'No',
            fechaSalida: orderData.fechaSalida || ''
        };
        setEditedData(currentData);
        setOriginalData(currentData);
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setEditedData(originalData);
    };

    const handleSave = async () => {
        try {
            const orderId = orderData.noOrden;
            const workshopId = orderData.workshopID;

            // Preparar payload para campos básicos
            const payload = {
                data: {
                    services_request: editedData.serviciosSolicitados,
                    requiere_bill: editedData.requiereFactura === 'Sí',
                    checkout_date: editedData.fechaSalida || null
                }
            };

            // Guardar campos básicos
            await orderService.updateOrder(orderId, payload, workshopId);
            updateOrderData('serviciosSolicitados', editedData.serviciosSolicitados);
            updateOrderData('requiereFactura', editedData.requiereFactura);
            updateOrderData('fechaSalida', editedData.fechaSalida);

            notifySuccess('Orden actualizada exitosamente');
            setIsEditMode(false);
            setOriginalData(editedData);
        } catch (error) {
            console.error('Error guardando orden:', error);
            notifyError('Error al guardar la orden');
        }
    };

    // Funciones para Repairs (Trabajos Realizados)
    const handleAddRepair = () => {
        setEditingRepair(null);
        setShowRepairForm(true);
    };

    const handleEditRepair = (repair) => {
        setEditingRepair(repair);
        setShowRepairForm(true);
    };

    // CREAR NUEVO TRABAJO
    const handleCreateRepair = async (repairData) => {
        try {
            const workshopId = orderData.workshopID;
            const orderId = orderData.noOrden;
            const employeeId = userID; // El user_id es el employee_id de quien está registrando

            // Convertir DD-MM-YYYY a YYYY-MM-DD
            const toAPIDate = (d) => {
                if (!d) return null;
                if (d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
                if (d.match(/^\d{2}-\d{2}-\d{4}/)) {
                    const [day, month, year] = d.split(/[\s-]/)[0].split('-');
                    return `${year}-${month}-${day}`;
                }
                return d;
            };

            const payload = {
                data: {
                    title: repairData.titulo,
                    description: repairData.description,
                    progress_state: repairData.progress_state,
                    labor_hand: repairData.labor_hand || 0,
                    init_date: toAPIDate(repairData.init_date) || new Date().toISOString().split('T')[0],
                    finish_date: toAPIDate(repairData.finish_date),
                    order_id: orderId,
                    employee_id: employeeId
                }
            };

            const payload2 = {
                data: {
                    process_state: "IN_PROGRESS"
                }
            }

            await orderService.createRepair(payload, workshopId);
            await orderService.updateOrder(orderId, payload2, workshopId);

            updateOrderData('processState', "IN_PROGRESS");
            notifySuccess('Trabajo creado exitosamente');
            setShowRepairForm(false);
            await fetchAllOrderDetails(orderData);
        } catch (error) {
            console.error('\n❌ ERROR:', error.response?.data);
            notifyError(`Error: ${error.response?.data?.error || error.response?.data?.['error-message'] || error.message}`);
        }
    };

    // EDITAR TRABAJO EXISTENTE
    const handleUpdateRepair = async (repairData) => {
        try {
            const workshopId = orderData.workshopID;

            // Convertir DD-MM-YYYY a YYYY-MM-DD
            const toAPIDate = (d) => {
                if (!d) return null;
                if (d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
                if (d.match(/^\d{2}-\d{2}-\d{4}/)) {
                    const [day, month, year] = d.split(/[\s-]/)[0].split('-');
                    return `${year}-${month}-${day}`;
                }
                return d;
            };

            const payload = {
                data: {
                    titulo: repairData.titulo,
                    description: repairData.description,
                    progress_state: repairData.progress_state,
                    labor_hand: parseFloat(repairData.labor_hand) || 0,
                    init_date: toAPIDate(repairData.init_date),
                    finish_date: toAPIDate(repairData.finish_date)
                }
            };

            await orderService.updateRepair(repairData.repair_id, payload, workshopId);
            notifySuccess('Trabajo actualizado exitosamente');
            setShowRepairForm(false);
            setEditingRepair(null);
            await fetchAllOrderDetails(orderData);
        } catch (error) {
            notifyError(`Error: ${error.response?.data?.error || error.response?.data?.['error-message'] || error.message}`);
            console.error('Response status:', error.response?.status);
            console.error('Response data:', error.response?.data);
            notifyError(`Error al actualizar trabajo: ${error.response?.data?.error || error.response?.data?.['error-message'] || error.message}`);
        }
    };

    const handleDeleteRepair = (repairId) => {
        setConfirmConfig({
            open: true,
            message: '¿Estás seguro de que deseas eliminar este trabajo?',
            onConfirm: async () => {
                try {
                    await orderService.deleteRepair(
                        repairId,
                        { is_deleted: 1 },
                        orderData.workshopID
                    );
                    setRepairs(prevRepairs => prevRepairs.filter(r => r.repair_id !== repairId));
                    notifySuccess('Trabajo eliminado correctamente.');
                    setConfirmConfig({ open: false, message: '', onConfirm: null });
                } catch (error) {
                    console.error('Error al eliminar trabajo:', error);
                    notifyError('No se pudo eliminar el trabajo.');
                }
            }
        });
    };

    // Funciones para Items (Refacciones)
    const handleAddItem = async () => {
        setEditingItem(null);
        setShowItemForm(true);
        await fetchSuppliers(orderData.workshopID);
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setShowItemForm(true);
    };


    // CREAR NUEVA REFACCIÓN
    const handleCreateItem = async (itemData, repairId) => {
        try {
            const workshopId = orderData.workshopID;

            const payload = {
                data: {
                    repair_id: parseInt(repairId),
                    supplier_id: parseInt(itemData.supplier_id),
                    folio: itemData.folio || '',
                    price: parseFloat(itemData.price) || 0,
                    description: itemData.description,
                    type: itemData.type,
                    created_at: itemData.created_at || new Date().toISOString(),
                    limit_date_to_pay: itemData.limit_date_to_pay || null
                }
            };

            await orderService.createRepairItem(payload, workshopId);
            notifySuccess('Refacción creada exitosamente');
            setShowItemForm(false);
            await fetchAllOrderDetails(orderData);
        } catch (error) {
            console.error('\n❌ ERROR:', error.response?.data);
            notifyError(`Error: ${error.response?.data?.error || error.response?.data?.['error-message'] || error.message}`);
        }
    };

    // EDITAR REFACCIÓN EXISTENTE
    const handleUpdateItem = async (itemData) => {
        try {
            const workshopId = orderData.workshopID;

            const payload = {
                data: {
                    supplier_id: parseInt(itemData.supplier_id),
                    folio: itemData.folio || '',
                    price: parseFloat(itemData.price) || 0,
                    description: itemData.description,
                    type: itemData.type,
                    created_at: itemData.created_at,
                    limit_date_to_pay: itemData.limit_date_to_pay
                }
            };

            await orderService.updateRepairItem(itemData.item_id, payload, workshopId);
            notifySuccess('Refacción actualizada exitosamente');
            setShowItemForm(false);
            setEditingItem(null);
            await fetchAllOrderDetails(orderData);
        } catch (error) {
            console.error('\n❌ ERROR:', error.response?.data);
            notifyError(`Error: ${error.response?.data?.error || error.response?.data?.['error-message'] || error.message}`);
        }
    };

    const handleDeleteItem = (itemId) => {
        setConfirmConfig({
            open: true,
            message: '¿Estás seguro de que deseas eliminar esta refacción?',
            onConfirm: async () => {
                try {
                    await orderService.deleteRepairItem(
                        itemId,
                        { is_deleted: 1 },
                        orderData.workshopID
                    );
                    setItems(prevItems => prevItems.filter(i => i.item_id !== itemId));
                    notifySuccess('Refacción eliminada correctamente.');
                    setConfirmConfig({ open: false, message: '', onConfirm: null });
                } catch (error) {
                    console.error('Error al eliminar refacción:', error);
                    notifyError('No se pudo eliminar la refacción.');
                }
            }
        });
    };

    const handleCreateSupplierFromItemModal = async (supplierData) => {
        const workshopId = orderData.workshopID;

        if (!workshopId) {
            throw new Error('No se pudo identificar el taller');
        }

        const createdSupplier = await supplierService.createSupplier(supplierData, workshopId);
        const refreshedSuppliers = await fetchSuppliers(workshopId);

        const createdId = createdSupplier?.id || createdSupplier?._id?.id || createdSupplier?._id;
        if (createdId) {
            return String(createdId);
        }

        const matchedSupplier = refreshedSuppliers.find((supplier) =>
            supplier.name === supplierData.name && supplier.email === supplierData.email,
        );

        return matchedSupplier?.id ? String(matchedSupplier.id) : '';
    };

    const handleReassignClient = () => {
        // Validar que haya cambio
        if (selectedClientId === clientId) {
            notifyWarning('No has cambiado el cliente');
            return;
        }

        const selectedClient = allClients.find(c => String(c.id) === String(selectedClientId));
        const clientName = selectedClient?.company_name ||
            `${selectedClient?.name.split(' ')[0] || ''} ${selectedClient?.first_lastname || ''}`;

        // Validar que existe
        if (!selectedClient) {
            notifyError('Error: No se encontró el cliente seleccionado');
            console.error('Cliente no encontrado. ID:', selectedClientId, 'Clientes:', allClients);
            return;
        }

        openConfirm(
            `¿Reasignar esta orden a ${clientName}?`,
            async () => {
                try {
                    const workshopId = orderData.workshopID;

                    const payload = {
                        data: {
                            client_id: parseInt(selectedClientId)
                        }
                    };

                    await carService.updateVehicle(carId, payload, workshopId);
                    notifySuccess('Cliente reasignado exitosamente');
                    setClientId(selectedClientId);
                    setOriginalClientId(selectedClientId);
                    setHasClientChanged(false);

                    await getDataClient(orderData, selectedClientId);
                } catch (error) {
                    console.error('Error reasignando cliente:', error);

                    if (error.response?.status === 404) {
                        notifyError('Cliente no encontrado');
                    } else if (error.response?.status === 403) {
                        notifyError('No tienes permiso para reasignar');
                    } else if (error.response?.status === 500) {
                        notifyError('Error del servidor. Intenta de nuevo');
                    } else {
                        notifyError(`Error: ${error.response?.data?.message || 'No se pudo reasignar'}`);
                    }
                }
            }
        );
    };

    const handleReturnToProgress = () => {
        openConfirm(
            '¿Devolver la orden a "En progreso"?',
            async () => {
                try {
                    const workshopId = orderData.workshopID;
                    const orderId = orderData.noOrden;

                    const payload = {
                        data: {
                            process_state: 'IN_PROGRESS',
                            is_payment: false
                        }
                    };

                    await orderService.updateOrderProcessState(orderId, payload, workshopId);
                    notifySuccess('Orden devuelta a "En progreso"');
                    updateOrderData('estadoProceso', 'En progreso');
                    updateOrderData('processState', "IN_PROGRESS");
                    await fetchAllOrderDetails(orderData);
                } catch (error) {
                    console.error('Error devolviendo orden:', error);
                    notifyError('Error al devolver la orden');
                }
            }
        );
    };

    const handleCompleteOrder = () => {
        openConfirm(
            '¿Marcar orden como "Completa" (sin pago)?',
            async () => {
                try {
                    const workshopId = orderData.workshopID;
                    const orderId = orderData.noOrden;
                    const now = new Date();

                    const payload = {
                        data: {
                            process_state: 'COMPLETE_NOT_PAY',
                            is_payment: false
                        }
                    };

                    await orderService.updateOrderProcessState(orderId, payload, workshopId);
                    notifySuccess('Orden marcada como "Completa"');
                    updateOrderData('estadoProceso', 'Completo');
                    updateOrderData('processState', 'COMPLETE_NOT_PAY');
                    updateOrderData('fechaSalida', formatTimestamp(now));
                    await fetchAllOrderDetails(orderData);
                } catch (error) {
                    console.error('Error completando orden:', error);
                    notifyError('Error al completar la orden');
                }
            }
        );
    };

    const handleMarkAsPaid = () => {
        // Validar que la orden esté completa
        if (orderData.processState !== 'COMPLETE_NOT_PAY') {
            notifyWarning('La orden debe estar en estado "Completo" antes de marcarla como pagada');
            return;
        }

        openConfirm(
            '¿Marcar orden como "Completa y pagada"?',
            async () => {
                try {
                    const workshopId = orderData.workshopID;
                    const orderId = orderData.noOrden;

                    const payload = {
                        data: {
                            process_state: 'COMPLETE_AND_PAY',
                            is_payment: true
                        }
                    };

                    await orderService.updateOrderProcessState(orderId, payload, workshopId);
                    notifySuccess('Orden marcada como "Completa y pagada"');
                    updateOrderData('estadoProceso', 'Completo y pagado');
                    updateOrderData('processState', 'COMPLETE_AND_PAY');
                    updateOrderData('isPayment', 'Si');
                    await fetchAllOrderDetails(orderData);
                } catch (error) {
                    console.error('Error marcando como pagada:', error);
                    notifyError('Error al marcar como pagada');
                }
            }
        );
    };


    const columnsRepairs = React.useMemo(() => [
        { Header: 'Título', accessor: 'titulo' },
        { Header: 'Descripción', accessor: 'description' },
        { Header: 'Estado', accessor: 'progress_state' },
        { Header: 'Fecha de entrada', accessor: 'init_date' },
        { Header: 'Fecha de finalización', accessor: 'finish_date' },
        { Header: 'Mano de Obra', accessor: 'labor_hand' },
    ], [isOrderComplete]);

    const columnsItems = React.useMemo(() => [
        { Header: 'Folio', accessor: 'folio' },
        { Header: 'Descripción', accessor: 'description' },
        { Header: 'Proveedor', accessor: 'supplier_name' },
        { Header: 'Precio', accessor: 'price' },
        { Header: 'Tipo', accessor: 'type' },
        { Header: 'Fecha de compra', accessor: 'created_at' },
        { Header: 'Fecha Límite Pago', accessor: 'limit_date_to_pay' },
    ], [isOrderComplete]);


    if (!isOpen) return null;

    return (
        <>
            {/* Sticky Header con botones de edición */}
            <div className="sticky-section-header">
                <h3>Información de la Orden</h3>
                <div className="header-actions">
                    {!isEditMode ? (
                        <button
                            className="btn-edit-inline"
                            onClick={handleEdit}
                            disabled={isOrderComplete}
                            title={isOrderComplete ? 'No se puede editar una orden completa' : 'Editar orden'}
                        >
                            <FaEdit /> Editar
                        </button>
                    ) : (
                        <>
                            <button className="btn-save" onClick={handleSave}>
                                Guardar
                            </button>
                            <button className="btn-cancel" onClick={handleCancel}>
                                Cancelar
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Grid de información editable */}
            <div className="info-grid-editable">

                {confirmConfig.open && (
                    <div className="modal-overlay" onClick={handleCancelConfirm}>
                        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Confirmar acción</h2>
                            </div>
                            <div className="modal-body">
                                <p>{confirmConfig.message}</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn-cancel" onClick={handleCancelConfirm}>
                                    Cancelar
                                </button>
                                <button className="btn-save" onClick={handleConfirmAction}>
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Fecha de ingreso - readonly */}
                <div className="form-group">
                    <label className="date-input-label">Fecha de ingreso</label>
                    <input
                        type="text"
                        className="input-readonly"
                        disabled
                        value={orderData.fechaIngreso || 'Fecha no registrada'}
                    />
                </div>

                {/* Estado del proceso - solo lectura */}
                <div className="form-group">
                    <label className="date-input-label">Estado del proceso</label>
                    <input
                        type="text"
                        className="input-readonly"
                        disabled
                        value={orderData.estadoProceso || 'Programado'}
                    />
                </div>

                {/* Servicios solicitados - editable como textarea */}
                <div className="form-group">
                    <label className="date-input-label">Servicios solicitados *</label>
                    {isEditMode ? (
                        <textarea
                            className="input-editable"
                            value={editedData.serviciosSolicitados || ''}
                            onChange={(e) => setEditedData({ ...editedData, serviciosSolicitados: e.target.value })}
                            placeholder="Descripción de servicios..."
                            rows="3"
                        />
                    ) : (
                        <textarea
                            className="input-readonly"
                            disabled
                            value={orderData.serviciosSolicitados || 'No se obtuvo ningún servicio solicitado'}
                            rows="3"
                        />
                    )}
                </div>

                {/* Requiere factura */}
                <div className="form-group">
                    <label className="date-input-label">Requiere factura</label>
                    {isEditMode ? (
                        <select
                            className="input-editable"
                            value={editedData.requiereFactura || 'No'}
                            onChange={(e) => setEditedData({ ...editedData, requiereFactura: e.target.value })}
                        >
                            <option value="No">No</option>
                            <option value="Sí">Sí</option>
                        </select>
                    ) : (
                        <input
                            type="text"
                            className="input-readonly"
                            disabled
                            value={orderData.requiereFactura || 'No'}
                        />
                    )}
                </div>

                {/* Fecha de salida - condicional */}
                <div className="form-group">
                    <label className="date-input-label">Fecha de salida</label>
                    {isEditMode ? (
                        <input
                            type="date"
                            className="input-editable"
                            value={editedData.fechaSalida || ''}
                            onChange={(e) => setEditedData({ ...editedData, fechaSalida: e.target.value })}
                        />
                    ) : (
                        <input
                            type="text"
                            className="input-readonly"
                            disabled
                            value={orderData.fechaSalida || 'Pendiente'}
                        />
                    )}
                </div>
            </div>

            {/* Cliente asignado - funcionalidad de reasignación */}
            {/* <div className="order-data">
                <label className='date-input-label'>Cliente asignado:</label>
                <div className="client-display">
                    <select
                        className="date-label"
                        value={selectedClientId}
                        onChange={(e) => {
                            setSelectedClientId(e.target.value);
                            setHasClientChanged(String(e.target.value) !== String(originalClientId));
                        }}
                        disabled={isOrderComplete}
                    >
                        {allClients.map(client => (
                            <option key={client.id} value={client.id}>
                                {client.company_name ||
                                    `${client.name.split(' ')[0]} ${client.first_lastname}`}
                            </option>
                        ))}
                    </select>
                    <button
                        className="reassing-btn"
                        onClick={handleReassignClient}
                        disabled={!hasClientChanged || isOrderComplete}
                        title={isOrderComplete ? 'No se puede reasignar cliente en una orden completa' : 'Reasignar cliente'}
                    >
                        <GrSync />
                    </button>
                </div>
            </div> */}

            {/* Botones de información */}
            <div className="info-actions-row">
                <button className='btn-info' onClick={() => {
                    getDataCar(orderData).then((data) => {
                        if (data) {
                            isOpenCarModal(true);
                            setCarData(data);
                        }
                    });
                }}>
                    📋 Información del vehículo
                </button>
                <button className='btn-info' onClick={() => {
                    getDataClient(orderData).then((data) => {
                        if (data) {
                            isOpenClientModal(true);
                            setClientData(data);
                        }
                    });
                }}>
                    👤 Información del cliente
                </button>
            </div>

            {/* Sticky Header - Trabajos Realizados */}
            <div className="sticky-section-header">
                <h3>Trabajos Realizados</h3>
                <div className="header-actions">
                    <button
                        className="btn-add-small"
                        onClick={handleAddRepair}
                        disabled={isOrderComplete}
                        title={isOrderComplete ? 'No se pueden agregar trabajos a una orden completa' : 'Agregar trabajo'}
                    >
                        + Agregar trabajo
                    </button>
                </div>
            </div>

            <div className="info-table">
                <GenericTable
                    columns={columnsRepairs}
                    data={repairs}
                    actionList={['edit', 'delete']}
                    actionHandlers={{
                        edit: (repair) => {
                            if (isOrderComplete) {
                                notifyWarning('No se pueden editar trabajos en una orden completa');
                                return;
                            }
                            handleEditRepair(repair);
                        },
                        delete: (repair) => {
                            if (isOrderComplete) {
                                notifyWarning('No se pueden eliminar trabajos en una orden completa');
                                return;
                            }
                            handleDeleteRepair(repair.repair_id);
                        },
                    }}
                />
                <label className="total-label">Total mano de obra: <strong>{`$${totalRepairs} MXN`}</strong></label>
            </div>

            {/* Sticky Header - Refacciones e Insumos */}
            <div className="sticky-section-header">
                <h3>Refacciones e Insumos</h3>
                <div className="header-actions">
                    <button
                        className="btn-add-small"
                        onClick={handleAddItem}
                        disabled={isOrderComplete}
                        title={isOrderComplete ? 'No se pueden agregar refacciones a una orden completa' : 'Agregar refacción'}
                    >
                        + Agregar refacción
                    </button>
                </div>
            </div>

            <div className="info-table">
                <GenericTable
                    columns={columnsItems}
                    data={items}
                    actionList={['edit', 'delete']}
                    actionHandlers={{
                        edit: (item) => {
                            if (isOrderComplete) {
                                notifyWarning('No se pueden editar refacciones en una orden completa');
                                return;
                            }
                            handleEditItem(item);
                        },
                        delete: (item) => {
                            if (isOrderComplete) {
                                notifyWarning('No se pueden eliminar refacciones en una orden completa');
                                return;
                            }
                            handleDeleteItem(item.item_id);
                        },
                    }}
                />
                <label className="total-label">Total de refacciones: <strong>{`$${totalItems} MXN`}</strong></label>
            </div>

            <div className="order-actions-footer">
                <div className="actions-row">
                    <button
                        className="btn-calculate"
                        onClick={savePrices}
                        disabled={isOrderComplete}
                        title={isOrderComplete ? 'No se puede calcular mano de obra en una orden completa' : 'Calcular mano de obra'}
                    >
                        Calcular mano de obra
                    </button>

                    {/* Botones para estado "En progreso" */}
                    {orderData.processState === 'IN_PROGRESS' && (
                        <>
                            <button className="btn-complete" onClick={handleCompleteOrder}>
                                Marcar como completa
                            </button>
                        </>
                    )}

                    {/* Botones para estado "En revisión" */}
                    {orderData.processState === 'REVIEW' && (
                        <>
                            <button className="btn-return" onClick={handleReturnToProgress}>
                                Devolver a progreso
                            </button>
                            <button className="btn-complete" onClick={handleCompleteOrder}>
                                Marcar como completa
                            </button>
                        </>
                    )}

                    {/* Botón para estado "Completo - Pendiente de pago" */}
                    {orderData.processState === 'COMPLETE_NOT_PAY' && (
                        <button className="btn-paid" onClick={handleMarkAsPaid}>
                            Marcar como pagado
                        </button>
                    )}
                </div>

                <div className="total-price">
                    <h3>TOTAL A PAGAR: <span>{totalPrice}</span></h3>
                    {orderData.processState === 'COMPLETE_AND_PAY' && (
                        <div className="payment-status paid">
                            ✅ PAGADO
                        </div>
                    )}
                    {orderData.processState === 'COMPLETE_NOT_PAY' && (
                        <div className="payment-status unpaid">
                            ⚠️ PENDIENTE DE PAGO
                        </div>
                    )}
                    {(orderData.processState !== 'COMPLETE_AND_PAY' && orderData.processState !== 'COMPLETE_NOT_PAY') && (
                        <div className="payment-status in-progress">
                            🔄 EN PROCESO
                        </div>
                    )}
                </div>
            </div>

            {clientModal && clientData && <ClientInfo
                isOpen={clientModal}
                onClose={isOpenClientModal}
                clientData={clientData}
                workshopID={orderData.workshopID}
            />}

            {carModal && carData && <CarInfo
                isOpen={carModal}
                onClose={isOpenCarModal}
                car={carData}
                workshopID={orderData.workshopID}
            />}

            {/* Modal para agregar/editar Repairs */}
            {showRepairForm && <RepairFormModal
                isOpen={showRepairForm}
                onClose={() => {
                    setShowRepairForm(false);
                    setEditingRepair(null);
                }}
                onSave={editingRepair ? handleUpdateRepair : handleCreateRepair}
                repairData={editingRepair}
            />}

            {/* Modal para agregar/editar Items */}
            {showItemForm && <ItemFormModal
                isOpen={showItemForm}
                onClose={() => {
                    setShowItemForm(false);
                    setEditingItem(null);
                }}
                onSave={editingItem ? handleUpdateItem : handleCreateItem}
                itemData={editingItem}
                suppliers={allSuppliers}
                repairs={repairs}
                supplierErrorMessage={supplierErrorMessage}
                onCreateSupplier={handleCreateSupplierFromItemModal}
            />}

        </>
    );
};

// Componente RepairFormModal
const RepairFormModal = ({ isOpen, onClose, onSave, repairData }) => {
    const [formData, setFormData] = React.useState({
        repair_id: '',
        titulo: '',
        description: '',
        progress_state: 'SCHEDULE',
        labor_hand: '',
        init_date: '',
        finish_date: ''
    });

    React.useEffect(() => {
        if (repairData) {
            // Convertir DD-MM-YYYY a YYYY-MM-DD para input type="date"
            const toInputDate = (dateStr) => {
                if (!dateStr) return '';
                const datePart = dateStr.split(' ')[0]; // "DD-MM-YYYY HH:MM..." -> "DD-MM-YYYY"
                if (datePart.match(/^\d{2}-\d{2}-\d{4}$/)) {
                    const [day, month, year] = datePart.split('-');
                    return `${year}-${month}-${day}`; // "YYYY-MM-DD"
                }
                return datePart;
            };

            setFormData({
                repair_id: repairData.repair_id || '',
                titulo: repairData.titulo || '',
                description: repairData.description || '',
                progress_state: repairData.progress_state_code || 'SCHEDULE',
                labor_hand: repairData.labor_hand || '',
                init_date: toInputDate(repairData.init_date),
                finish_date: toInputDate(repairData.finish_date)
            });
        } else {
            setFormData({
                repair_id: '',
                titulo: '',
                description: '',
                progress_state: 'SCHEDULE',
                labor_hand: '',
                init_date: new Date().toISOString().split('T')[0],
                finish_date: ''
            });
        }
    }, [repairData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        // El modal NO decide, simplemente pasa los datos
        // El componente padre determinará qué hacer
        onSave(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{repairData ? 'Editar Trabajo' : 'Nuevo Trabajo'}</h2>
                    <button className="close-button" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body">
                    {/* Información básica */}
                    <div className="form-group">
                        <label>Título del trabajo</label>
                        <input
                            type="text"
                            value={formData.titulo}
                            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                            placeholder="Ej: Cambio de aceite, Reparación de frenos..."
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Descripción detallada</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Describe el trabajo realizado en detalle..."
                            rows="4"
                            required
                        />
                    </div>

                    {/* Estado y costo */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Estado del trabajo</label>
                            <select
                                value={formData.progress_state}
                                onChange={(e) => setFormData({ ...formData, progress_state: e.target.value })}
                                required
                            >
                                <option value="SCHEDULE">Programado</option>
                                <option value="IN_PROGRESS">En progreso</option>
                                <option value="REVIEW">Revisión</option>
                                <option value="COMPLETE_NOT_PAY">Completo</option>
                                <option value="COMPLETE_AND_PAY">Completo y pagado</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Costo de mano de obra (MXN)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.labor_hand}
                                onChange={(e) => setFormData({ ...formData, labor_hand: e.target.value })}
                                placeholder="0.00"
                                required
                            />
                        </div>
                    </div>

                    {/* Fechas */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Fecha de inicio</label>
                            <input
                                type="date"
                                value={formData.init_date}
                                onChange={(e) => setFormData({ ...formData, init_date: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Fecha de finalización</label>
                            <input
                                type="date"
                                value={formData.finish_date}
                                onChange={(e) => setFormData({ ...formData, finish_date: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-save">
                            {repairData ? 'Guardar cambios' : 'Añadir trabajo'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Componente ItemFormModal
const ItemFormModal = ({ isOpen, onClose, onSave, itemData, suppliers, repairs, supplierErrorMessage, onCreateSupplier }) => {
    const [formData, setFormData] = React.useState({
        item_id: '',
        repair_id: '',
        supplier_id: '',
        folio: '',
        price: '',
        description: '',
        type: 'Refaccion',
        created_at: '',
        limit_date_to_pay: ''
    });
    const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = React.useState(false);
    const [newSupplierData, setNewSupplierData] = React.useState({
        name: '',
        manager_name: '',
        address: '',
        phone: '',
        RFC: '',
        secondary_phone: '',
        email: '',
    });

    React.useEffect(() => {
        if (itemData) {
            // Convertir DD-MM-YYYY a YYYY-MM-DD para input type="date"
            const toInputDate = (dateStr) => {
                if (!dateStr) return '';
                const datePart = dateStr.split(' ')[0]; // "DD-MM-YYYY HH:MM..." -> "DD-MM-YYYY"
                if (datePart.match(/^\d{2}-\d{2}-\d{4}$/)) {
                    const [day, month, year] = datePart.split('-');
                    return `${year}-${month}-${day}`; // "YYYY-MM-DD"
                }
                return datePart;
            };

            setFormData({
                item_id: itemData.item_id || '',
                repair_id: itemData.repair_id || '',
                supplier_id: itemData.supplier_id || '',
                folio: itemData.folio || '',
                price: itemData.price || '',
                description: itemData.description || '',
                type: itemData.type_code || itemData.type || 'Refaccion',
                created_at: toInputDate(itemData.created_at),
                limit_date_to_pay: toInputDate(itemData.limit_date_to_pay)
            });
        } else {
            setFormData({
                item_id: '',
                repair_id: repairs && repairs.length > 0 ? repairs[0].repair_id : '',
                supplier_id: '',
                folio: '',
                price: '',
                description: '',
                type: 'Refaccion',
                created_at: new Date().toISOString().split('T')[0],
                limit_date_to_pay: ''
            });
        }
    }, [itemData, repairs]);

    const hasSuppliers = Array.isArray(suppliers) && suppliers.length > 0;
    const requiresSupplierCreation = !itemData && !hasSuppliers;

    const handleSubmit = (e) => {
        e.preventDefault();

        if (requiresSupplierCreation) {
            notifyWarning('Debes registrar un proveedor antes de agregar una refacción.');
            return;
        }

        if (!itemData && formData.repair_id) {
            // Crear: pasar también el repair_id
            onSave(formData, formData.repair_id);
        } else {
            // Editar
            onSave(formData);
        }
    };

    const handleCreateSupplier = async (e) => {
        e.preventDefault();

        if (!newSupplierData.name || !newSupplierData.manager_name || !newSupplierData.address || !newSupplierData.phone || !newSupplierData.RFC || !newSupplierData.email) {
            notifyWarning('Todos los campos obligatorios del proveedor deben ser llenados.');
            return;
        }

        try {
            const createdSupplierId = await onCreateSupplier(newSupplierData);
            notifySuccess('Proveedor agregado correctamente.');
            setIsCreateSupplierModalOpen(false);
            setNewSupplierData({
                name: '',
                manager_name: '',
                address: '',
                phone: '',
                RFC: '',
                secondary_phone: '',
                email: '',
            });

            if (createdSupplierId) {
                setFormData((prev) => ({ ...prev, supplier_id: createdSupplierId }));
            }
        } catch (error) {
            console.error('Error al crear proveedor desde refacciones:', error);
            notifyError('No se pudo crear el proveedor.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{itemData ? 'Editar Refacción' : 'Nueva Refacción'}</h2>
                    <button className="close-button" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body">
                    {/* Trabajo asociado */}
                    {!itemData && (
                        <div className="form-group">
                            <label>Trabajo asociado *</label>
                            <select
                                value={formData.repair_id}
                                onChange={(e) => setFormData({ ...formData, repair_id: e.target.value })}
                                required
                            >
                                <option value="">Seleccione un trabajo...</option>
                                {repairs && repairs.map((repair) => (
                                    <option key={repair.repair_id} value={repair.repair_id}>
                                        {repair.titulo}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Proveedor y Tipo */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Proveedor</label>
                            <div className="form-select-with-button">
                                <select
                                    value={formData.supplier_id}
                                    onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                                    disabled={requiresSupplierCreation}
                                    required
                                    className="form-select-input"
                                >
                                    <option value="">Seleccione un proveedor...</option>
                                    {suppliers && suppliers.map((supplier) => (
                                        <option key={supplier.id} value={supplier.id}>
                                            {supplier.name}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    className="add-inline-btn"
                                    onClick={() => setIsCreateSupplierModalOpen(true)}
                                    title="Agregar nuevo proveedor"
                                >
                                    +
                                </button>
                            </div>
                            {!itemData && supplierErrorMessage && (
                                <div className="form-error-message">⚠️ {supplierErrorMessage}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Tipo de artículo</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                required
                            >
                                <option value="Refaccion">Refacción</option>
                                <option value="T-O-T">T-O-T</option>
                                <option value="UPB">UPB</option>
                                <option value="SupplierWorkshop">Insumo del taller</option>
                            </select>
                        </div>
                    </div>

                    {/* Folio y Precio */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Folio o número de parte</label>
                            <input
                                type="text"
                                value={formData.folio}
                                onChange={(e) => setFormData({ ...formData, folio: e.target.value })}
                                placeholder="Ej: ABC123, REF-001..."
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Precio unitario (MXN)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                placeholder="0.00"
                                required
                            />
                        </div>
                    </div>

                    {/* Descripción detallada */}
                    <div className="form-group">
                        <label>Descripción detallada</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Describe la refacción o insumo en detalle..."
                            rows="4"
                            required
                        />
                    </div>

                    {/* Fechas */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Fecha de compra</label>
                            <input
                                type="date"
                                value={formData.created_at}
                                onChange={(e) => setFormData({ ...formData, created_at: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Fecha límite de pago</label>
                            <input
                                type="date"
                                value={formData.limit_date_to_pay}
                                onChange={(e) => setFormData({ ...formData, limit_date_to_pay: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-save" disabled={requiresSupplierCreation}>
                            {itemData ? 'Guardar cambios' : 'Añadir refacción'}
                        </button>
                    </div>
                </form>

                {isCreateSupplierModalOpen && (
                    <div className="modal-overlay" onClick={() => setIsCreateSupplierModalOpen(false)}>
                        <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Nuevo Proveedor</h2>
                                <button className="close-button" onClick={() => setIsCreateSupplierModalOpen(false)}>✕</button>
                            </div>

                            <form onSubmit={handleCreateSupplier} className="modal-body">
                                <div className="form-group">
                                    <label>Nombre del proveedor *</label>
                                    <input
                                        type="text"
                                        value={newSupplierData.name}
                                        onChange={(e) => setNewSupplierData({ ...newSupplierData, name: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Nombre del encargado *</label>
                                    <input
                                        type="text"
                                        value={newSupplierData.manager_name}
                                        onChange={(e) => setNewSupplierData({ ...newSupplierData, manager_name: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Dirección *</label>
                                    <input
                                        type="text"
                                        value={newSupplierData.address}
                                        onChange={(e) => setNewSupplierData({ ...newSupplierData, address: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Teléfono *</label>
                                        <input
                                            type="tel"
                                            value={newSupplierData.phone}
                                            onChange={(e) => setNewSupplierData({ ...newSupplierData, phone: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Teléfono secundario</label>
                                        <input
                                            type="tel"
                                            value={newSupplierData.secondary_phone}
                                            onChange={(e) => setNewSupplierData({ ...newSupplierData, secondary_phone: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>RFC *</label>
                                        <input
                                            type="text"
                                            value={newSupplierData.RFC}
                                            onChange={(e) => setNewSupplierData({ ...newSupplierData, RFC: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Correo electrónico *</label>
                                        <input
                                            type="email"
                                            value={newSupplierData.email}
                                            onChange={(e) => setNewSupplierData({ ...newSupplierData, email: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn-cancel" onClick={() => setIsCreateSupplierModalOpen(false)}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-save">
                                        Guardar proveedor
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
